import {readWireMinimumPrice} from "@/features/pricing";
import {DigitalProduct} from "../../domain/digital-product.entity";
import {
  DigitalProductSummary,
  ProductContent,
  SalesInsights,
  CustomerPurchase,
  PaginatedResponse,
  CreateDigitalProductInput,
  DigitalProductCurrencyInput,
  UpdateDigitalProductInput,
  AddOn,
  BookingQuestion,
  SaveAddOnsInput,
} from "../../domain/digital-product.types";
import {
  V2DigitalProductListResponse,
  V2GetProductResponse,
  V2GetContentResponse,
  V2SalesInsightsResponse,
  V2CustomerPurchasesResponse,
  V2CreateDigitalProductRequest,
  V2UpdateDigitalProductRequest,
  V2AddOnResponse,
  V2GetAddOnsResponse,
  V2GetAddOnResponse,
  V2SaveAddOnsRequest,
  V2BookingQuestionRequest,
  V2BookingQuestionResponse,
} from "./digital-product.mapper.types";

/**
 * DigitalProductMapperV2
 *
 * Maps between v2 API response shapes (camelCase, `name`) and domain entities.
 * v2 responses are closer to domain shape, so mapping is mostly passthrough.
 */
export class DigitalProductMapperV2 {
  /**
   * Map v2 list response to domain summary list
   */
  static toSummaryList(
    response: V2DigitalProductListResponse
  ): PaginatedResponse<DigitalProductSummary> {
    const data = response.data?.data ?? [];

    return {
      data: data.map((item) => ({
        id: item.id,
        name: item.name,
        tinyUrl: item.tinyUrl,
        imageUrl: item.imageUrl,
        currency: item.currency,
        amount: item.amount,
        totalPurchases: item.totalPurchases,
        totalRevenue: item.totalRevenue,
      })),
      total: response.data?.total ?? 0,
      hasNextPage: response.data?.hasNextPage ?? false,
      hasPrevPage: response.data?.hasPrevPage ?? false,
    };
  }

  /**
   * Map v2 single product response to domain entity
   */
  static toDomain(response: V2GetProductResponse): DigitalProduct {
    const data = response.data;

    return DigitalProduct.create({
      id: data.id,
      userId: data.userId,
      name: data.name,
      slug: data.slug,
      tinyUrl: data.tinyUrl,
      description: data.description,
      hideFromProfile: data.hideFromProfile,
      hideVariantFromProfile: data.hideVariantFromProfile,
      urlRedirect: data.urlRedirect,
      redirectType: data.redirectType,
      telegramGroupChatId: data.telegramGroupChatId,
      statusId: data.statusId,
      statusName: data.statusName,
      requestPhoneNumber: data.requestPhoneNumber,
      purchaseLimit: data.purchaseLimit,
      reference: data.reference,
      callPrice: data.callPrice,
      callPriceCurrency: data.callPriceCurrency,
      enableCurrencyConversion: data.enableCurrencyConversion,
      isPayWhatYouWant: data.isPayWhatYouWant ?? false,
      // Absent on a response that only carries the base scalars; the entity synthesises
      // a single row from those rather than reporting the product as unpriced.
      // `minAmount` is the wire's spelling of the Minimum Price, and a Decimal string
      // besides; `minCallPrice` is the domain's. The rename stops here.
      currencies: data.currencies?.map((currency) => ({
        currency: currency.currency,
        amount: currency.amount,
        minCallPrice: readWireMinimumPrice(currency.minAmount),
        isAutoConverted: currency.isAutoConverted,
        isBase: currency.isBase,
      })),
      bookingQuestions: (data.bookingQuestions ?? []).map((q) =>
        this.toBookingQuestion(q)
      ),
      coverImage: undefined,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    });
  }

  /**
   * Map a stored booking question to the domain. Questions saved before the API
   * enforced `answer_type` still come back as camelCase `answerType`, so read both.
   */
  private static toBookingQuestion(q: V2BookingQuestionResponse): BookingQuestion {
    return {
      question: q.question,
      answerType: q.answer_type ?? q.answerType ?? "short-text",
      required: q.required,
      ...(q.options ? {options: q.options} : {}),
    };
  }

  /**
   * Map a domain booking question to the request shape. The API names the type
   * `answer_type` and rejects an empty `options` list, so options go only when present.
   */
  private static toBookingQuestionRequest(q: BookingQuestion): V2BookingQuestionRequest {
    return {
      question: q.question,
      answer_type: q.answerType,
      required: q.required,
      ...(q.options && q.options.length > 0 ? {options: q.options} : {}),
    };
  }

  /**
   * Map v2 content response to a list of domain ProductContent.
   * A product can hold multiple files; `data` is an array (or null/empty).
   */
  static toContentList(response: V2GetContentResponse): ProductContent[] {
    return (response.data ?? []).map((item) => ({
      id: item.id,
      productId: item.productId,
      fileName: item.fileName,
      fileType: item.fileType,
      fileSize: item.fileSize,
      humanReadableFileSize: item.humanReadableFileSize,
      mediaUrl: item.mediaUrl,
      createdAt: item.createdAt,
    }));
  }

  /**
   * Map a single v2 add-on response object to a domain AddOn.
   * All currencies are preserved (with `isManuallyAdded`); consumers filter
   * to manually-added ones when reconstructing coach input.
   */
  private static toAddOn(item: V2AddOnResponse): AddOn {
    return {
      id: item.id,
      name: item.name,
      options: item.options.map((option) => ({
        id: option.id,
        name: option.name,
        currencies: option.currencies.map((currency) => ({
          currency: currency.currency,
          amount: currency.amount,
          isManuallyAdded: currency.isManuallyAdded,
        })),
      })),
    };
  }

  /**
   * Map a v2 add-ons list response to domain AddOns (empty when none).
   */
  static toAddOnList(response: V2GetAddOnsResponse): AddOn[] {
    return (response.data ?? []).map((item) => this.toAddOn(item));
  }

  /**
   * Map a v2 single add-on response to a domain AddOn (null when absent).
   */
  static toSingleAddOn(response: V2GetAddOnResponse): AddOn | null {
    return response.data ? this.toAddOn(response.data) : null;
  }

  /**
   * Map a domain save-add-ons input to the v2 request body (shared by POST/PUT).
   * Keeps ids where present (upsert), drops auto-converted currencies
   * (`isManuallyAdded === false`) so the backend regenerates conversions, and
   * leaves free options as an empty `currencies` array.
   */
  static toSaveAddOnsRequest(input: SaveAddOnsInput): V2SaveAddOnsRequest {
    return {
      hideVariantFromProfile: input.hideVariantFromProfile,
      addOns: input.addOns.map((addOn) => ({
        ...(addOn.id ? {id: addOn.id} : {}),
        name: addOn.name,
        options: addOn.options.map((option) => ({
          ...(option.id ? {id: option.id} : {}),
          name: option.name,
          currencies: option.currencies
            .filter((currency) => currency.isManuallyAdded !== false)
            .map((currency) => ({
              currency: currency.currency,
              amount: currency.amount,
            })),
        })),
      })),
    };
  }

  /**
   * Map v2 sales insights response to domain SalesInsights
   */
  static toSalesInsights(response: V2SalesInsightsResponse): SalesInsights {
    return {
      totalSales: response.data?.totalSales ?? 0,
      totalRevenueByCurrency: response.data?.totalRevenueByCurrency ?? {},
    };
  }

  /**
   * Map v2 customer purchases response to domain
   */
  static toCustomerPurchases(
    response: V2CustomerPurchasesResponse
  ): PaginatedResponse<CustomerPurchase> {
    const data = response.data?.data ?? [];

    return {
      data: data.map((item) => ({
        id: item.id,
        firstName: item.firstName,
        lastName: item.lastName,
        emailAddress: item.emailAddress,
        phoneNumber: item.phoneNumber,
        currency: item.currency,
        amount: item.amount,
        charges: item.charges,
        reference: item.reference,
        statusId: item.statusId,
        statusName: item.statusName,
        paymentChannel: item.paymentChannel,
        discountCode: item.discountCode,
        isDiscounted: item.isDiscounted,
        chargeBearer: item.chargeBearer,
        bookingQuestions: item.bookingQuestions,
        createdAt: item.createdAt,
      })),
      total: response.data?.total ?? 0,
      hasNextPage: response.data?.hasNextPage ?? false,
      hasPrevPage: response.data?.hasPrevPage ?? false,
    };
  }

  /**
   * Map a currency row to the v2 request shape.
   *
   * Digital Products put EVERY row into `currencies[]`, base row included — unlike live
   * events and booking services, which split the base row into top-level scalars. See
   * `docs/adr/0002-pricing-slice-uniform-currency-rows.md`.
   *
   * Only Pay What You Want has a Minimum Price; the key is omitted rather than sent as a
   * zero the other two models would have to mean something by.
   */
  private static toCurrencyRequest(currency: DigitalProductCurrencyInput) {
    return {
      callPriceCurrency: currency.callPriceCurrency,
      callPrice: currency.callPrice,
      ...(currency.minCallPrice === undefined
        ? {}
        : {minCallPrice: currency.minCallPrice}),
    };
  }

  /**
   * The API's redirect enum is `url | telegram` only, so the domain's `none` must not
   * reach it. Omitting the key is how no redirect is said, on create and update alike.
   */
  private static toRedirectTypeRequest(
    redirectType: CreateDigitalProductInput["redirectType"]
  ): Pick<V2CreateDigitalProductRequest, "redirectType"> {
    return redirectType === "url" || redirectType === "telegram" ? {redirectType} : {};
  }

  /**
   * Map domain create input to v2 API request
   */
  static toCreateRequest(
    input: CreateDigitalProductInput
  ): V2CreateDigitalProductRequest {
    return {
      name: input.name,
      description: input.description,
      currencies: input.currencies.map((c) => this.toCurrencyRequest(c)),
      isPayWhatYouWant: input.isPayWhatYouWant,
      hideFromProfile: input.hideFromProfile,
      coverImage: input.coverImage,
      urlRedirect: input.urlRedirect,
      ...this.toRedirectTypeRequest(input.redirectType),
      telegramGroupChatId: input.telegramGroupChatId,
      requestPhoneNumber: input.requestPhoneNumber,
      // Omitted outright when the coach set no cap — see `toPurchaseLimitPayload`.
      ...(input.purchaseLimit === undefined ? {} : {purchaseLimit: input.purchaseLimit}),
      bookingQuestions: input.bookingQuestions?.map((q) =>
        this.toBookingQuestionRequest(q)
      ),
      enableCurrencyConversion: input.enableCurrencyConversion,
      selectedCurrencies: input.selectedCurrencies,
      automationEmailIds: input.automationEmailIds,
    };
  }

  /**
   * Map domain update input to v2 API request
   */
  static toUpdateRequest(
    input: UpdateDigitalProductInput
  ): V2UpdateDigitalProductRequest {
    return {
      productId: input.productId,
      name: input.name,
      description: input.description,
      currencies: input.currencies.map((c) => this.toCurrencyRequest(c)),
      isPayWhatYouWant: input.isPayWhatYouWant,
      hideFromProfile: input.hideFromProfile,
      coverImage: input.coverImage,
      urlRedirect: input.urlRedirect,
      ...this.toRedirectTypeRequest(input.redirectType),
      telegramGroupChatId: input.telegramGroupChatId,
      requestPhoneNumber: input.requestPhoneNumber,
      // Omitted outright when the coach set no cap — see `toPurchaseLimitPayload`.
      ...(input.purchaseLimit === undefined ? {} : {purchaseLimit: input.purchaseLimit}),
      bookingQuestions: input.bookingQuestions?.map((q) =>
        this.toBookingQuestionRequest(q)
      ),
      enableCurrencyConversion: input.enableCurrencyConversion,
      selectedCurrencies: input.selectedCurrencies,
      automationEmailIds: input.automationEmailIds,
    };
  }
}
